import React, { useState } from "react";
import { Toast } from "./Toast";
import {
  CustomerUpdateForm,
  CustomerUpdateFormProps,
} from "@/components/CustomerUpdate/CustomerUpdateForm";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Common/Icon";
import { IconNames, TerminologyOptions } from "@/types/v2";
import { Tooltip } from "@/components/Common/Tooltip";
import { useStore } from "@/stores/useStore";
import { getCardSection } from "@/lib/cards";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/Base/Separator";
import { AnalyticsLocationProvider } from "@/providers/AnalyticsLocationProvider";
import { useInboxPanel } from "@/hooks/useInboxPanel";

type CustomerUpdateToastProps = CustomerUpdateFormProps & {
  onDismiss?: () => void;
};

export const CustomerUpdateToast = React.forwardRef<
  React.ElementRef<typeof Toast>,
  CustomerUpdateToastProps
>(
  (
    {
      cardData,
      shop,
      serviceAdvisors,
      initialValues,
      onSubmit,
      onSnooze,
      snoozeTime,
      onDismiss,
      snoozeAllEnabled,
      onSnoozeAll,
      submitting,
      location,
      ...props
    },
    ref
  ) => {
    const { terminolize } = useTerminologyReplacer();
    const myCustomerUpdates = useStore((s) => s.myCustomerUpdates);
    const cards = useStore((s) => s.cards);
    const isCustomerUpdatesTabVisible = useStore(
      (s) => s.isCustomerUpdatesTabVisible
    );
    const { isOpen: isInboxPanelOpen } = useInboxPanel();
    const [isMinimized, setIsMinimized] = useState(false);
    const [internalIndex, setInternalIndex] = useState(0);

    // Get current customer update and card data based on internal navigation
    const currentCustomerUpdate = myCustomerUpdates?.[internalIndex];
    const currentCardData = currentCustomerUpdate
      ? cards?.find((c) => c.id === currentCustomerUpdate.cardId)
      : cardData;
    const currentInitialValues = currentCustomerUpdate || initialValues;

    // Internal navigation handlers
    const handleInternalPrevious = () => {
      const newIndex = Math.max(0, internalIndex - 1);
      setInternalIndex(newIndex);
    };

    const handleInternalNext = () => {
      const newIndex = Math.min(
        myCustomerUpdates.length - 1,
        internalIndex + 1
      );
      setInternalIndex(newIndex);
    };

    // Hide toast only when BOTH inbox panel is open AND customer updates tab is visible
    if (isInboxPanelOpen && isCustomerUpdatesTabVisible) {
      return null;
    }

    return (
      <Toast
        ref={ref}
        variant="default"
        className={cn(
          "overflow-visible rounded-xl p-0 shadow-xl",
          isMinimized ? "w-96" : "w-full"
        )}
        {...props}
        duration={Infinity}
        onSwipeEnd={onSnoozeAll}
      >
        {myCustomerUpdates.length > 1 && (
          <div className="absolute -left-4 -top-4 z-10 flex h-8 min-w-8 items-center justify-center rounded-full bg-destructive p-2 text-sm text-destructive-foreground">
            {myCustomerUpdates.length}
          </div>
        )}

        {/* Navigation arrows */}
        {myCustomerUpdates.length > 1 && !isMinimized && (
          <>
            {/* Previous button */}
            {internalIndex !== 0 && (
              <Tooltip message="Previous Update" className="z-[100]">
                <button
                  className="absolute left-1/4 top-6 z-10 -translate-y-1/2 rounded-full bg-background/80 p-2 shadow-md transition-colors hover:bg-background disabled:opacity-50 disabled:hover:bg-background/80"
                  onClick={handleInternalPrevious}
                  disabled={internalIndex === 0}
                >
                  <Icon name={IconNames.chevronLeft} className="h-4 w-4" />
                </button>
              </Tooltip>
            )}

            {/* Next button */}
            {internalIndex !== myCustomerUpdates.length - 1 && (
              <Tooltip message="Next Update" className="z-[100]">
                <button
                  className="absolute right-1/4 top-6 z-10 -translate-y-1/2 rounded-full bg-background/80 p-2 shadow-md transition-colors hover:bg-background disabled:opacity-50 disabled:hover:bg-background/80"
                  onClick={handleInternalNext}
                  disabled={internalIndex === myCustomerUpdates.length - 1}
                >
                  <Icon name={IconNames.chevronRight} className="h-4 w-4" />
                </button>
              </Tooltip>
            )}
          </>
        )}
        <div className="absolute right-2 top-2 z-10 flex flex-row gap-2">
          <Tooltip message={"View All Customer Updates"} className={"z-[100]"}>
            <div className="h-6 w-6">
              <Link
                to={`/${shop?.id ?? ""}/customer-updates`}
                onClick={onDismiss}
              >
                <Icon name={IconNames.customerUpdate} />
              </Link>
            </div>
          </Tooltip>
          {!isMinimized && (
            <Tooltip message="Minimize" className={"z-[100]"}>
              <button onClick={() => setIsMinimized(true)} className="z-10">
                <span className="text-lg leading-none">
                  <Icon name={IconNames.minimize} size={14} />
                </span>
              </button>
            </Tooltip>
          )}
          <Tooltip message="Close" className={"z-[100]"}>
            <button onClick={onDismiss} className="z-10">
              <span className="text-lg leading-none">
                <Icon name={IconNames.x} size={14} />
              </span>
            </button>
          </Tooltip>
        </div>
        {isMinimized ? (
          <div
            className="flex w-full cursor-pointer items-center gap-3 rounded-t-md p-4 hover:bg-muted/50"
            onClick={() => setIsMinimized(false)}
          >
            {currentCardData && shop && (
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-2">
                  <Icon
                    name={IconNames.bell}
                    className={"h-6 w-6 fill-accent text-accent"}
                    size={20}
                  />
                  <h2 className="text-base font-medium">
                    Customer Update Reminder
                  </h2>
                </div>
                <div className="flex flex-row gap-2 text-gray-500">
                  <div className={"font-medium"}>
                    <span>
                      {terminolize(
                        "RO",
                        TerminologyOptions.repairOrder,
                        "acronym"
                      )}
                      #{" "}
                    </span>
                    <span>{currentCardData.cardNumber}</span>
                  </div>
                  <Separator orientation={"vertical"} className={"h-6"} />
                  <div>
                    {getCardSection(currentCardData, shop, "secondaryField")}
                  </div>

                  <Separator orientation={"vertical"} className={"h-6"} />
                  <div>
                    {getCardSection(currentCardData, shop, "tertiaryField")}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex max-h-[80vh] w-full flex-col overflow-hidden">
            <AnalyticsLocationProvider
              location={location}
              additionalData={{
                cardId: currentInitialValues.cardId,
                shopId: currentInitialValues.shopId,
                customerUpdateId: currentInitialValues.id,
              }}
            >
              <div className="overflow-y-auto p-4">
                <CustomerUpdateForm
                  initialValues={currentInitialValues}
                  onSubmit={onSubmit}
                  onSnooze={onSnooze}
                  cardData={currentCardData}
                  shop={shop}
                  serviceAdvisors={serviceAdvisors}
                  snoozeTime={snoozeTime}
                  snoozeAllEnabled={snoozeAllEnabled}
                  onSnoozeAll={onSnoozeAll}
                  submitting={submitting}
                  location={location}
                />
              </div>
            </AnalyticsLocationProvider>
          </div>
        )}
      </Toast>
    );
  }
);

CustomerUpdateToast.displayName = "CustomerUpdateToast";
